import classNames from "classnames";
import * as React from "react";
import { useEffect, useState } from "react";

import { Color, COLORS, Size, SIZES } from "../consts";
import styles from "./CircularBar.module.scss";

const safeDuration = styles.animationduration || "1000ms";

export const ANIMATION_DURATION = Number(
  safeDuration.slice(0, safeDuration.length - 2)
);

const MIN_PROGRESS = 0;
const MAX_PROGRESS = 100;
const DEFAULT_DELAY = 1000;

type PreviousProgressStyle = {
  stroke: string;
  opacity?: string;
};

export type CircularBarProps = {
  progress?: number;
  previousProgress?: number;
  badgeURL?: string;
  badgeOpacity?: number;
  trailClass?: string;
  trailColor?: Color | string;
  animateProgressOnly?: boolean;
  strokeWidth: number;
  className?: string;
  delay?: number;
  children?: React.ReactNode;
  color?: Color | string;
  size?: Size | string;
  isThemed?: boolean;
  previousProgressStyle?: PreviousProgressStyle;
  duration?: number;
  animate?: boolean;
};

export default function CircularBar({
  progress,
  previousProgress,
  badgeURL,
  badgeOpacity,
  trailClass,
  trailColor,
  animateProgressOnly,
  strokeWidth,
  className,
  delay = DEFAULT_DELAY,
  children,
  color,
  size,
  isThemed,
  previousProgressStyle,
  duration,
  animate
}: CircularBarProps) {
  const [currentProgress, setCurrentProgress] = useState(
    animate ? 0 : progress
  );
  const [prevProgress, setPrevProgress] = useState(
    animateProgressOnly ? previousProgress : 0
  );

  useEffect(() => {
    if (!animateProgressOnly) {
      const timeout = setTimeout(() => {
        setPrevProgress(previousProgress);
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [previousProgress, animateProgressOnly]);

  useEffect(() => {
    if (animate) {
      const timeout = setTimeout(() => {
        if (progress !== undefined && progress !== null) {
          setCurrentProgress(progress);
        }
      }, delay);

      return () => clearTimeout(timeout);
    }
  }, [progress, delay, animate]);

  const getRadius = () => {
    return 50 - strokeWidth / 2;
  };

  const getProgressStyle = () => {
    const diameter = Math.PI * 2 * getRadius();
    const truncatedProgress = Math.min(
      Math.max(currentProgress || 0, MIN_PROGRESS),
      MAX_PROGRESS
    );

    return {
      stroke: color,
      strokeDasharray: `${diameter}px ${diameter}px`,
      strokeDashoffset: `${((100 - truncatedProgress) / 100) * diameter}px`,
      transitionDuration: `${duration || ANIMATION_DURATION}ms`
    };
  };

  const getPreviousProgressStyle = (prevProgress: number) => {
    const diameter = Math.PI * 2 * getRadius();
    const truncatedProgress = Math.min(
      Math.max(prevProgress, MIN_PROGRESS),
      MAX_PROGRESS
    );
    const style = previousProgressStyle || {
      stroke: color,
      opacity: "0.3"
    };

    return {
      ...style,
      strokeDasharray: `${diameter}px ${diameter}px`,
      strokeDashoffset: `${((100 - truncatedProgress) / 100) * diameter}px`,
      transitionDuration: `${duration || ANIMATION_DURATION}ms`
    };
  };

  return (
    <div
      className={classNames(styles.container, className, {
        [styles.sizeLarge]: size === SIZES.LARGE,
        [styles.sizeMedium]: size === SIZES.MEDIUM,
        [styles.sizeSmall]: size === SIZES.SMALL,
        [styles.sizeXsmall]: size === SIZES.XSMALL,
        [styles.sizeXxsmall]: size === SIZES.XXSMALL
      })}
    >
      <svg className={styles.svg} viewBox="0 0 100 100">
        <circle
          className={classNames(trailClass, styles.circlePosition, {
            [styles.trailGreen]: !color || isThemed || color === COLORS.GREEN,
            [styles.trailTransparentWhite]: color === COLORS.WHITE,
            [styles.trailPrimary]: color === COLORS.LIGHT_GREEN,
            [styles.trailTransparent]: trailColor === COLORS.TRANSPARENT,
            [styles.trailWhite]: trailColor === COLORS.WHITE,
            [styles.trailNeutral]: color && !(color in COLORS)
          })}
          r={getRadius()}
          strokeWidth={strokeWidth}
          fillOpacity={0}
        />
        <circle
          className={styles.circle}
          r={getRadius()}
          strokeWidth={strokeWidth - 1}
          fillOpacity={0}
          style={getPreviousProgressStyle(prevProgress ?? 0)}
        />
        <circle
          className={styles.circle}
          r={getRadius()}
          strokeWidth={strokeWidth}
          fillOpacity={0}
          style={getProgressStyle()}
        />
      </svg>
      <div className={styles.badgeWrapper}>
        <div className={styles.badgeContainer}>
          {children ? (
            children
          ) : badgeURL ? (
            <img
              className={styles.badge}
              src={badgeURL}
              alt={""}
              style={{
                opacity: badgeOpacity
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
